import { useEffect, useMemo, useRef, useState } from "react";
import "../styles/ComboBox.css";

/**
 * Searchable input-select with keyboard navigation.
 *
 * Uses the shared `mv2-combobox` styles defined in `MasterViewV2.css`.
 *
 * Props:
 * - options:     [{ value, label }]
 * - value:       currently selected value
 * - onChange:    (value) => void
 * - placeholder: shown when nothing selected
 * - error:       red border when truthy
 * - disabled:    boolean
 * - className:   extra wrapper class
 */
export default function ComboBox({
  options = [],
  value,
  onChange,
  placeholder = "— select —",
  className = "",
  error = false,
  disabled = false,
}) {
  const [query,       setQuery]       = useState("");
  const [open,        setOpen]        = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [listPos,     setListPos]     = useState(null);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);
  const listRef  = useRef(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => { setHighlighted(0); }, [filtered.length]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Position the dropdown via getBoundingClientRect so it escapes any overflow:auto ancestor
  useEffect(() => {
    if (!open || !wrapRef.current) return;
    const update = () => {
      const r = wrapRef.current.getBoundingClientRect();
      setListPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[highlighted];
    item?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  const openDropdown  = () => { setQuery(""); setOpen(true); };
  const closeDropdown = () => { setQuery(""); setOpen(false); };

  const commit = (opt) => {
    if (disabled) return;
    onChange(opt.value);
    closeDropdown();
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") { openDropdown(); return; }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        setOpen(true);
        setQuery(e.key);
        return;
      }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, filtered.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
    if (e.key === "Enter")     { e.preventDefault(); if (filtered[highlighted]) commit(filtered[highlighted]); }
    if (e.key === "Escape")    { closeDropdown(); }
    if (e.key === "Tab")       { closeDropdown(); }
  };

  return (
    <div ref={wrapRef} className={`mv2-combobox${open ? " mv2-combobox--open" : ""}${error ? " mv2-combobox--error" : ""}${disabled ? " mv2-combobox--disabled" : ""}${className ? ` ${className}` : ""}`}>
      <div className="mv2-combobox-input-wrap">
        <input
          ref={inputRef}
          className="mv2-combobox-input"
          value={open ? query : (selected?.label || "")}
          placeholder={open ? "Type to filter…" : (selected?.label ? "" : placeholder)}
          disabled={disabled}
          readOnly={!open}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (!open && !disabled) openDropdown(); }}
          onMouseDown={(e) => { if (!open && !disabled) { e.preventDefault(); openDropdown(); } }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        <span className="mv2-combobox-arrow" onMouseDown={(e) => { if (disabled) return; e.preventDefault(); open ? closeDropdown() : openDropdown(); }}>
          <svg viewBox="0 0 10 6" width="10" height="6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <polyline points="1 1 5 5 9 1"/>
          </svg>
        </span>
      </div>
      {open && listPos && (
        <ul
          ref={listRef}
          className="mv2-combobox-list"
          style={{
            position: "fixed",
            top: listPos.top,
            left: listPos.left,
            width: listPos.width,
            zIndex: 5000,
            background: "#FFFFFF",
            border: "1px solid #D7D0BE",
            boxShadow: "0 6px 20px rgba(11,14,19,0.15)",
          }}
        >
          {filtered.length === 0 ? (
            <li className="mv2-combobox-empty">No matches</li>
          ) : (
            filtered.map((opt, i) => (
              <li
                key={`${opt.value}__${i}`}
                className={`mv2-combobox-option${i === highlighted ? " mv2-combobox-option--highlighted" : ""}${opt.value === value ? " mv2-combobox-option--selected" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); commit(opt); }}
                onMouseEnter={() => setHighlighted(i)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
